import React, { useState, useEffect } from 'react';
import {
  Layout,
  Typography,
  Card,
  Table,
  Space,
  Button,
  Tag,
  Statistic,
  Row,
  Col,
  Spin,
  message,
  Divider,
  Grid
} from 'antd';
import ReactECharts from 'echarts-for-react';
import {
  ArrowLeftOutlined,
  TrophyOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { parallel } from '../utils/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const PortfolioDetail = () => {
  const [loading, setLoading] = useState(true);
  const [portfolioStat, setPortfolioStat] = useState(null);
  const [etfList, setEtfList] = useState(null);
  const [timeSeries, setTimeSeries] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();

  useEffect(() => {
    const fetchPortfolioData = async () => {
      try {
        const portfolioId = localStorage.getItem('portfolioId');

        if (!portfolioId) {
          message.error(t('portfolio.portfolioIdNotFound'));
          navigate('/dashboard');
          return;
        }

        // 构建API请求URL
        const statUrl = `/api/etfportfoliolist/id/portfolioStatistics?portfolioId=${portfolioId}`;
        const etfListUrl = `/api/etfportfoliolist?portfolioId=${portfolioId}`;
        const timeSeriesUrl = `/api/etfportfoliolist/id/portfolioTimeseries?portfolioId=${portfolioId}&period=0`;

        // 使用API工具类的并行请求功能
        // 401错误会自动被拦截并跳转到登录页面
        const [statData, etfData, timeSeriesData] = await parallel([
          { url: statUrl },
          { url: etfListUrl },
          { url: timeSeriesUrl }
        ]);

        setPortfolioStat(statData);
        setEtfList(etfData);
        setTimeSeries(timeSeriesData);
      } catch (error) {
        console.error('Error fetching portfolio data:', error);
        message.error(t('portfolio.loadDataError'));
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolioData();
  }, [navigate, t]);

  const handleBack = () => {
    navigate('/dashboard');
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === "NaN") return 'N/A';
    const numValue = parseFloat(value);
    const percentValue = numValue * 100; // Convert decimal to percentage
    return `${percentValue >= 0 ? '+' : ''}${percentValue.toFixed(2)}%`;
  };

  const formatExistingPercent = (value) => {
    if (value === null || value === undefined || value === "NaN") return 'N/A';
    // Remove the % sign and convert to number
    const numValue = parseFloat(value.replace('%', ''));
    return `${numValue >= 0 ? '+' : ''}${numValue.toFixed(2)}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getReturnColor = (value) => {
    if (value === null || value === undefined || value === "NaN") return '#1890ff';
    const numValue = parseFloat(value);
    return numValue >= 0 ? '#3f8600' : '#cf1322';
  };

  // 图表配置
  const getChartOption = () => {
    if (!timeSeries || timeSeries.length === 0) return {};

    const dates = timeSeries.map(item => {
      const date = new Date(item.Date);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    });

    // 计算回报率
    const returns = timeSeries.map((item, index) => {
      if (index === 0) return 0;
      return ((item.Value - timeSeries[0].Value) / timeSeries[0].Value * 100);
    });

    return {
      title: {
        text: t('portfolio.performanceChart', '投资组合表现'),
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        formatter: function (params) {
          const dataIndex = params[0].dataIndex;
          const returnValue = returns[dataIndex];
          return `<div style="font-weight: bold;">${params[0].axisValue}</div>
                  <div>回报率: ${returnValue >= 0 ? '+' : ''}${returnValue.toFixed(2)}%</div>`;
        }
      },
      legend: {
        data: [t('portfolio.returnValue', '回报率')],
        bottom: 0
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: {
          rotate: 45,
          interval: Math.floor(dates.length / 10) // 显示约10个标签
        }
      },
      yAxis: {
        type: 'value',
        name: t('portfolio.returnValue', '回报率(%)'),
        position: 'right',
        axisLabel: {
          formatter: function (value) {
            return `${value.toFixed(1)}%`;
          }
        }
      },
      series: [
        {
          name: t('portfolio.returnValue', '回报率'),
          type: 'line',
          data: returns,
          itemStyle: {
            color: returns[returns.length - 1] >= 0 ? '#52c41a' : '#ff4d4f'
          },
          lineStyle: {
            color: returns[returns.length - 1] >= 0 ? '#52c41a' : '#ff4d4f',
            width: 2
          },
          smooth: true
        }
      ],
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      dataZoom: [
        {
          type: 'inside',
          start: 70,
          end: 100
        },
        {
          start: 70,
          end: 100
        }
      ]
    };
  };

  // 持仓表格列配置
  const holdingsColumns = [
    {
      title: t('portfolio.weight', '权重'),
      dataIndex: 'Weight',
      key: 'weight',
      width: screens.xs ? 60 : 80,
      fixed: !screens.xs ? 'left' : false,
      render: (value) => (
        <Tag color={parseFloat(value) > 5 ? 'red' : 'blue'} style={{ fontSize: screens.xs ? '10px' : '12px' }}>
          {value}
        </Tag>
      ),
      sorter: (a, b) => parseFloat(a.Weight) - parseFloat(b.Weight),
    },
    {
      title: t('portfolio.ticker', '代码'),
      dataIndex: 'Ticker',
      key: 'ticker',
      width: screens.xs ? 60 : 100,
      fixed: !screens.xs ? 'left' : false,
    },
    {
      title: screens.xs ? t('portfolio.fundName', '名称') : t('portfolio.fundName', '基金名称'),
      dataIndex: 'FundName',
      key: 'fundName',
      width: screens.xs ? 120 : 300,
      ellipsis: true,
    },
    {
      title: screens.xs ? t('portfolio.assetClass', '类别') : t('portfolio.assetClass', '资产类别'),
      dataIndex: 'AssetClass',
      key: 'assetClass',
      width: screens.xs ? 60 : 100,
      render: (value) => (
        <Tag color={value === 'Equity' ? 'green' : 'orange'} style={{ fontSize: screens.xs ? '10px' : '12px' }}>
          {value === 'Equity' ? (screens.xs ? '股' : t('portfolio.equity', '股票')) : (screens.xs ? '债' : t('portfolio.fixedIncome', '债券'))}
        </Tag>
      ),
    },
    {
      title: t('portfolio.assetRegion', '地区'),
      dataIndex: 'AssetRegion',
      key: 'assetRegion',
      width: screens.xs ? 60 : 120,
      responsive: ['md'], // 在中等屏幕及以上显示
    },
    {
      title: t('portfolio.ter', '费用率'),
      dataIndex: 'TER',
      key: 'ter',
      width: screens.xs ? 60 : 80,
      render: (value) => `${value}%`,
      sorter: (a, b) => parseFloat(a.TER) - parseFloat(b.TER),
    },
    {
      title: screens.xs ? 'YTD' : t('portfolio.returnYtd', '年初至今回报'),
      dataIndex: 'ReturnYtd',
      key: 'returnYtd',
      width: screens.xs ? 60 : 120,
      render: (value) => {
        const numValue = parseFloat(value.replace('%', ''));
        return (
          <Text type={numValue >= 0 ? 'success' : 'danger'} style={{ fontSize: screens.xs ? '10px' : '12px' }}>
            {formatExistingPercent(value)}
          </Text>
        );
      },
      sorter: (a, b) => parseFloat(a.ReturnYtd.replace('%', '')) - parseFloat(b.ReturnYtd.replace('%', '')),
    },
    {
      title: screens.xs ? '1Y' : t('portfolio.return1Y', '1年回报'),
      dataIndex: 'Return1Y',
      key: 'return1Y',
      width: screens.xs ? 60 : 100,
      render: (value) => {
        const numValue = parseFloat(value.replace('%', ''));
        return (
          <Text type={numValue >= 0 ? 'success' : 'danger'} style={{ fontSize: screens.xs ? '10px' : '12px' }}>
            {formatExistingPercent(value)}
          </Text>
        );
      },
      sorter: (a, b) => parseFloat(a.Return1Y.replace('%', '')) - parseFloat(b.Return1Y.replace('%', '')),
    },
    {
      title: t('portfolio.dividendYield', '股息率'),
      dataIndex: 'DividendYield',
      key: 'dividendYield',
      width: screens.xs ? 60 : 100,
      responsive: ['lg'], // 在大屏幕及以上显示
      render: (value) => (
        <Text type="secondary" style={{ fontSize: screens.xs ? '10px' : '12px' }}>{value}</Text>
      ),
    },
    {
      title: t('portfolio.aum', '资产管理规模'),
      dataIndex: 'AUM',
      key: 'aum',
      width: screens.xs ? 60 : 100,
      responsive: ['lg'], // 在大屏幕及以上显示
      render: (value) => (
        <Text type="secondary" style={{ fontSize: screens.xs ? '10px' : '12px' }}>{value}</Text>
      ),
    },
  ];

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Header style={{
          background: '#fff',
          padding: '0 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
            {t('portfolio.title', '投资组合详情')}
          </Title>
          <LanguageSwitcher />
        </Header>
        <Content style={{ padding: '24px', background: '#f0f2f5' }}>
          <div style={{ textAlign: 'center', marginTop: '100px' }}>
            <Spin size="large" />
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: screens.xs ? '0 12px' : '0 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: screens.xs ? 'wrap' : 'nowrap',
        gap: screens.xs ? '8px' : '0'
      }}>
        <div style={{
          flex: 1,
          minWidth: screens.xs ? '200px' : 'auto',
          overflow: 'hidden'
        }}>
          <Title
            level={screens.xs ? 5 : 3}
            style={{
              margin: 0,
              color: '#1890ff',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
            title={portfolioStat?.PortfolioName || 'N/A'}
          >
            {screens.xs ? t('portfolio.title', '投资组合详情') : `${t('portfolio.title', '投资组合详情')} - ${portfolioStat?.PortfolioName || 'N/A'}`}
          </Title>
          {screens.xs && (
            <Text
              type="secondary"
              style={{
                fontSize: '12px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
                maxWidth: '200px'
              }}
              title={portfolioStat?.PortfolioName || 'N/A'}
            >
              {portfolioStat?.PortfolioName || 'N/A'}
            </Text>
          )}
        </div>
        <Space size={screens.xs ? 'small' : 'middle'}>
          <LanguageSwitcher />
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            size={screens.xs ? 'small' : 'middle'}
          >
            {screens.xs ? '' : t('portfolio.back', '返回')}
          </Button>
        </Space>
      </Header>

      <Content style={{
        padding: screens.xs ? '12px' : '24px',
        background: '#f0f2f5'
      }}>
        {/* 第一部分：投资组合关键数据 */}
        <Card
          title={
            <Space>
              <TrophyOutlined />
              {t('portfolio.keyStatistics', '关键统计数据')}
            </Space>
          }
          extra={
            <Text type="secondary">
              ID: {portfolioStat?.PortfolioId?.slice(0, 8)}...
            </Text>
          }
          style={{ marginBottom: '24px' }}
        >
          {/* 基本信息 */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={12} sm={12} md={6}>
              <Statistic
                title={t('portfolio.portfolioName', '投资组合名称')}
                value={portfolioStat?.PortfolioName || 'N/A'}
                valueStyle={{
                  fontSize: screens.xs ? '14px' : '16px',
                  wordBreak: 'break-word'
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Statistic
                title={t('portfolio.portfolioType', '投资组合类型')}
                value={portfolioStat?.PortfolioType || 'N/A'}
                valueStyle={{
                  fontSize: screens.xs ? '14px' : '16px'
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Statistic
                title={t('portfolio.currency', '货币')}
                value={portfolioStat?.Currency || 'N/A'}
                valueStyle={{
                  fontSize: screens.xs ? '14px' : '16px'
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Statistic
                title={t('portfolio.valuationDate', '估值日期')}
                value={formatDate(portfolioStat?.ValuationDate)}
                valueStyle={{
                  fontSize: screens.xs ? '14px' : '16px'
                }}
              />
            </Col>
          </Row>

          {/* 关键指标 */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={12} sm={12} md={6}>
              <Statistic
                title={t('portfolio.numberETFs', 'ETF数量')}
                value={portfolioStat?.NumberETFs || 0}
                suffix={t('portfolio.etfs', '个')}
                prefix={<TrophyOutlined />}
                valueStyle={{
                  fontSize: screens.xs ? '14px' : '16px'
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Statistic
                title={t('portfolio.weightedDividendYield', '加权股息率')}
                value={portfolioStat?.WeightedDividendYield || 0}
                formatter={formatPercent}
                valueStyle={{
                  color: '#52c41a',
                  fontSize: screens.xs ? '14px' : '16px'
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Statistic
                title={t('portfolio.weightedTER', '加权费用率')}
                value={portfolioStat?.WeightedTER || 0}
                suffix="%"
                precision={2}
                valueStyle={{
                  color: '#cf1322',
                  fontSize: screens.xs ? '14px' : '16px'
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Statistic
                title={t('portfolio.overallRating', '整体评级')}
                value={portfolioStat?.OverallRating || 0}
                suffix="/ 5"
                prefix={<TrophyOutlined />}
                valueStyle={{
                  fontSize: screens.xs ? '14px' : '16px'
                }}
              />
            </Col>
          </Row>

          {/* 关键回报指标 */}
          <Divider>{t('portfolio.keyReturns', '关键回报指标')}</Divider>
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Statistic
                title={t('portfolio.ytdReturn', '年初至今回报')}
                value={portfolioStat?.ReturnYtd || 0}
                formatter={formatPercent}
                valueStyle={{ color: getReturnColor(portfolioStat?.ReturnYtd) }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('portfolio.mtdReturn', '月度回报')}
                value={portfolioStat?.ReturnMtd || 0}
                formatter={formatPercent}
                valueStyle={{ color: getReturnColor(portfolioStat?.ReturnMtd) }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('portfolio.sinceInceptionReturn', '成立来回报')}
                value={portfolioStat?.ReturnSinceInception || 0}
                formatter={formatPercent}
                valueStyle={{ color: getReturnColor(portfolioStat?.ReturnSinceInception) }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('portfolio.return1M', '1个月回报')}
                value={portfolioStat?.Return1M || 0}
                formatter={formatPercent}
                valueStyle={{ color: getReturnColor(portfolioStat?.Return1M) }}
              />
            </Col>
          </Row>

          {/* 详细回报指标 */}
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Statistic
                title={t('portfolio.return3M', '3个月回报')}
                value={portfolioStat?.Return3M || 0}
                formatter={formatPercent}
                valueStyle={{ color: getReturnColor(portfolioStat?.Return3M) }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('portfolio.return6M', '6个月回报')}
                value={portfolioStat?.Return6M || 0}
                formatter={formatPercent}
                valueStyle={{ color: getReturnColor(portfolioStat?.Return6M) }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('portfolio.return1Y', '1年回报')}
                value={portfolioStat?.Return1Y || 0}
                formatter={formatPercent}
                valueStyle={{ color: getReturnColor(portfolioStat?.Return1Y) }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('portfolio.return3Y', '3年回报')}
                value={portfolioStat?.Return3Y || 0}
                formatter={formatPercent}
                valueStyle={{ color: getReturnColor(portfolioStat?.Return3Y) }}
              />
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Statistic
                title={t('portfolio.return5Y', '5年回报')}
                value={portfolioStat?.Return5Y || 0}
                formatter={formatPercent}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('portfolio.return10Y', '10年回报')}
                value={portfolioStat?.Return10Y || 0}
                formatter={formatPercent}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('portfolio.volatilityRating', '波动率评级')}
                value={portfolioStat?.VolatilityRating || 0}
                suffix="/ 5"
                prefix={<SafetyOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('portfolio.drawdownRating', '回撤评级')}
                value={portfolioStat?.DrawdownRating || 0}
                suffix="/ 5"
                prefix={<SafetyOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Col>
          </Row>

          {/* 风险指标 */}
          <Divider>{t('portfolio.riskMetrics', '风险指标')}</Divider>
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Statistic
                title={t('portfolio.volatility1Y', '1年波动率')}
                value={portfolioStat?.Volatility1Y || 0}
                formatter={formatPercent}
                valueStyle={{ color: '#faad14' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('portfolio.volatility3Y', '3年波动率')}
                value={portfolioStat?.Volatility3Y || 0}
                formatter={formatPercent}
                valueStyle={{ color: '#faad14' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('portfolio.volatilitySinceInception', '成立来波动率')}
                value={portfolioStat?.VolatilitySinceInception || 0}
                formatter={formatPercent}
                valueStyle={{ color: '#faad14' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('portfolio.maximumDrawdown', '最大回撤')}
                value={portfolioStat?.MaximumDrawdown || 0}
                formatter={formatPercent}
                valueStyle={{ color: '#cf1322' }}
              />
            </Col>
          </Row>

          {/* 最终评级指标 */}
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title={t('portfolio.overallRating', '整体评级')}
                value={portfolioStat?.OverallRating || 0}
                suffix="/ 5"
                prefix={<TrophyOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={18}>
              <div style={{ textAlign: 'center', paddingTop: '16px' }}>
                <Text type="secondary">
                  {t('portfolio.ratingDescription', '综合评级基于波动率、回撤和整体表现评估')}
                </Text>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 第二部分：持仓表格 */}
        <Card
          title={
            <Space>
              <ThunderboltOutlined />
              {t('portfolio.holdings', '持仓明细')}
            </Space>
          }
          style={{ marginBottom: '24px' }}
        >
          <Table
            columns={holdingsColumns}
            dataSource={etfList || []}
            rowKey="Ticker"
            scroll={{ x: screens.xs ? 800 : 1400 }}
            size={screens.xs ? 'small' : 'middle'}
            pagination={{
              pageSize: screens.xs ? 20 : 30,
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: false,
              simple: screens.xs
            }}
            summary={(pageData) => {
              const totalWeight = pageData.reduce((sum, item) =>
                sum + parseFloat(item.Weight || 0), 0
              );
              return (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>{t('portfolio.total', '合计')}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <Text strong>{t('portfolio.totalHoldings', '总持仓数')}: {pageData.length}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} colSpan={7}>
                      <Text strong>{t('portfolio.totalWeight', '总权重')}: {totalWeight.toFixed(2)}%</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>

        {/* 第三部分：回报走势图 */}
        <Card
          title={
            <Space>
              <LineChartOutlined />
              {screens.xs ? t('portfolio.performanceChart', '走势') : t('portfolio.performanceChart', '投资组合表现走势')}
            </Space>
          }
        >
          <ReactECharts
            option={getChartOption()}
            style={{ height: screens.xs ? '300px' : '500px' }}
            notMerge={true}
            lazyUpdate={true}
          />
        </Card>
      </Content>
    </Layout>
  );
};

export default PortfolioDetail;